import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const ThemeContext = createContext();

// Default themes (fallback)
const defaultAdminTheme = {
  sidebar: {
    backgroundColor: '#2B4B6A',
    textColor: '#FFFFFF',
    hoverColor: '#3D5F80',
    activeColor: '#1E3A52',
    logoUrl: '',
    siteTitle: 'Trados — The Trusted Trade Platform'
  },
  header: {
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937'
  },
  page: {
    backgroundColor: '#F9FAFB',
    textColor: '#1F2937',
    titleColor: '#111827',
    titleSize: '24px'
  },
  externalPages: {
    headerBackgroundColor: '#FFFFFF',
    headerTextColor: '#1F2937',
    pageBackgroundColor: '#F9FAFB',
    footerBackgroundColor: '#FFFFFF',
    footerTextColor: '#6B7280'
  },
  footer: {
    poweredByText: 'Powered by Trados',
    poweredByUrl: 'https://www.trados.co'
  },
  cards: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    textColor: '#374151',
    headerColor: '#111827'
  },
  buttons: {
    primaryColor: '#2B4B6A',
    primaryTextColor: '#FFFFFF',
    secondaryColor: '#F58A5A',
    secondaryTextColor: '#FFFFFF'
  },
  tables: {
    headerBackgroundColor: '#F9FAFB',
    headerTextColor: '#6B7280',
    rowBackgroundColor: '#FFFFFF',
    rowTextColor: '#374151',
    borderColor: '#E5E7EB'
  },
  filters: {
    backgroundColor: '#D4F6FF',
    maxClosedWidth: '200px',
    maxOpenWidth: '300px'
  }
};

const defaultUserTheme = {
  sidebar: {
    backgroundColor: '#2B4B6A',
    textColor: '#FFFFFF',
    hoverColor: '#3D5F80',
    activeColor: '#1E3A52',
    logoUrl: '',
    siteTitle: 'Trados — The Trusted Trade Platform'
  },
  page: {
    backgroundColor: '#F9FAFB',
    textColor: '#1F2937'
  },
  welcomeBox: {
    backgroundColor: '#F3F4F6',
    textColor: '#1F2937',
    titleColor: '#111827',
    borderColor: '#E5E7EB'
  },
  upgradeBox: {
    backgroundColor: '#FFF7ED',
    textColor: '#1F2937',
    titleColor: '#111827',
    borderColor: '#FDBA74',
    buttonColor: '#F58A5A',
    buttonTextColor: '#FFFFFF'
  },
  verificationBox: {
    backgroundColor: '#F0FDF4',
    textColor: '#166534',
    pendingBackgroundColor: '#FFFBEB',
    pendingTextColor: '#92400E',
    rejectedBackgroundColor: '#FEF2F2',
    rejectedTextColor: '#991B1B'
  },
  opportunitiesBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    badgeColor: '#2B4B6A'
  },
  announcementsBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    linkColor: '#2B4B6A'
  },
  quickActionsBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    buttonColor: '#2B4B6A',
    buttonTextColor: '#FFFFFF'
  },
  dealsBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    priceColor: '#059669',
    headerBackgroundColor: '#2B4B6A'
  },
  supportBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    buttonColor: '#2B4B6A'
  },
  educationBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    playButtonColor: '#2B4B6A'
  },
  requestsBox: {
    backgroundColor: '#FFFFFF',
    textColor: '#374151',
    titleColor: '#111827',
    borderColor: '#E5E7EB',
    statusApprovedColor: '#059669',
    statusPendingColor: '#D97706',
    statusRejectedColor: '#DC2626'
  },
  accordionHeader: {
    backgroundColor: '#F9FAFB',
    textColor: '#1F2937',
    hoverColor: '#F3F4F6',
    borderColor: '#E5E7EB'
  },
  quoteDetail: {
    primaryColor: '#2B4B6A',
    secondaryColor: '#F58A5A',
    stepCardBg: '#FFFFFF',
    stepCardBorder: '#E5E7EB',
    activeStepBg: '#F3F4F6',
    lockedStepText: '#9CA3AF',
    notStartedHeaderBg: '#F9FAFB',
    pendingHeaderBg: '#FEF9C3',
    approvedHeaderBg: '#DCFCE7',
    deniedHeaderBg: '#FEE2E2'
  },
  dashboardWidgetHeader: {
    backgroundColor: '#F0FDF4'
  }
};

export const ThemeProvider = ({ children }) => {
  // Seed from a cached theme so the app paints the correct colors INSTANTLY on
  // boot (no full-screen spinner, no flash). fetchTheme() refreshes the cache.
  const [adminTheme, setAdminTheme] = useState(() => {
    try { const c = localStorage.getItem('trados_admin_theme'); if (c) return { ...defaultAdminTheme, ...JSON.parse(c) }; } catch (e) {}
    return defaultAdminTheme;
  });
  const [userTheme, setUserTheme] = useState(() => {
    try { const c = localStorage.getItem('trados_user_theme'); if (c) return { ...defaultUserTheme, ...JSON.parse(c) }; } catch (e) {}
    return defaultUserTheme;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const res = await api.get('/theme');
      if (res.data.admin_theme) {
        // Deep merge admin theme
        setAdminTheme(prevTheme => {
          const newTheme = { ...prevTheme };
          Object.keys(res.data.admin_theme).forEach(key => {
            if (typeof res.data.admin_theme[key] === 'object' && res.data.admin_theme[key] !== null) {
              newTheme[key] = { ...prevTheme[key], ...res.data.admin_theme[key] };
            } else {
              newTheme[key] = res.data.admin_theme[key];
            }
          });
          try { localStorage.setItem('trados_admin_theme', JSON.stringify(newTheme)); } catch (e) {}
          return newTheme;
        });
      }
      if (res.data.user_theme) {
        // Deep merge user theme
        setUserTheme(prevTheme => {
          const newTheme = { ...prevTheme };
          Object.keys(res.data.user_theme).forEach(key => {
            if (typeof res.data.user_theme[key] === 'object' && res.data.user_theme[key] !== null) {
              newTheme[key] = { ...prevTheme[key], ...res.data.user_theme[key] };
            } else {
              newTheme[key] = res.data.user_theme[key];
            }
          });
          try { localStorage.setItem('trados_user_theme', JSON.stringify(newTheme)); } catch (e) {}
          return newTheme;
        });
      }
    } catch (error) {
      console.log('Using default theme');
    } finally {
      setLoading(false);
    }
  };

  const updateAdminTheme = (newTheme) => {
    setAdminTheme({ ...adminTheme, ...newTheme });
  };

  const updateUserTheme = (newTheme) => {
    setUserTheme({ ...userTheme, ...newTheme });
  };

  const refreshTheme = () => {
    fetchTheme();
  };

  return (
    <ThemeContext.Provider value={{
      adminTheme,
      userTheme,
      updateAdminTheme,
      updateUserTheme,
      refreshTheme,
      loading,
      defaultAdminTheme,
      defaultUserTheme
    }}>
      {/* Render immediately with the cached/default theme — never block the whole
          app (incl. public pages) behind the /theme fetch. fetchTheme() updates
          colors in place when it resolves. `loading` stays in context for any
          consumer that wants it. */}
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
